import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Collapse
} from '@mui/material';
import { ContentCut } from '@mui/icons-material';
import { useAudioPlayer } from '@contexts/AudioPlayerContext';
import AudioPlayerControls from './AudioPlayerControls';
import AudioProgressBar from './AudioProgressBar';
import AudioVolumeControl from './AudioVolumeControl';
import TimeClockEditor from './TimeClockEditor';
import PlaybackSpeedSelector from './PlaybackSpeedSelector';
import ClipExportDialogController from '@controllers/ClipExportDialogController';
import { isIOS } from '@utils/platform/deviceDetection';

export default function AudioPlayer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { currentEpisode } = useAudioPlayer();
  const [clipDialogOpen, setClipDialogOpen] = useState(false);

  // Version from package.json (Bug #13 fix)
  const version = import.meta.env.VITE_APP_VERSION || '1.0.0';

  // Keep a two-row mobile player above the iOS home-indicator area.
  const mobilePlayerContentHeight = 104;
  const desktopPlayerHeight = 60;
  const showContent = Boolean(currentEpisode);
  const playerHeight = showContent
    ? {
        xs: `calc(${mobilePlayerContentHeight}px + env(safe-area-inset-bottom))`,
        sm: desktopPlayerHeight
      }
    : 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: playerHeight,
        bgcolor: 'background.paper',
        borderTop: showContent ? 1 : 0,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1100,
        transition: 'height 0.3s ease-in-out, border-top 0.3s ease-in-out',
        overflow: 'hidden',
        boxSizing: 'border-box',
        pb: { xs: 'env(safe-area-inset-bottom)', sm: 0 }
      }}
    >
      <Collapse in={showContent} timeout={300} sx={{ width: '100%' }}>
        <Container
          maxWidth="lg"
          disableGutters
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, sm: 2 },
            height: { xs: mobilePlayerContentHeight, sm: desktopPlayerHeight },
            width: '100%',
            px: { xs: 1, sm: 2 }
          }}
        >
          {isMobile && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                width: '100%',
                minWidth: 0
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0, px: 0.5 }}>
                <AudioProgressBar />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <AudioPlayerControls />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0, px: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {currentEpisode?.title}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setClipDialogOpen(true)}
                  aria-label="Export audio clip"
                  title="Export audio clip"
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                >
                  <ContentCut fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          {!isMobile && (
            <>
              {/* Left: Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <AudioPlayerControls />
                <TimeClockEditor />
              </Box>

              {/* Center: Progress Bar */}
              <Box sx={{ flexGrow: 1, minWidth: 0, mx: 2 }}>
                <AudioProgressBar />
              </Box>

              {/* Right: Episode title and controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ maxWidth: 300, color: 'text.primary' }}
                >
                  {currentEpisode?.title}
                </Typography>
                {!isIOS() && <AudioVolumeControl />}
                <PlaybackSpeedSelector />
                <IconButton
                  size="small"
                  onClick={() => setClipDialogOpen(true)}
                  aria-label="Export audio clip"
                  title="Export audio clip"
                  sx={{ color: 'text.secondary' }}
                >
                  <ContentCut fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}

          {/* Version Badge */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 8,
              color: 'text.disabled',
              fontSize: '0.65rem',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            v{version}
          </Typography>
        </Container>
      </Collapse>

      {/* Clip Export Dialog */}
      <ClipExportDialogController
        open={clipDialogOpen}
        onClose={() => setClipDialogOpen(false)}
        episode={currentEpisode}
      />
    </Box>
  );
}
